import { useCallback, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { Apollo } from 'src/gql/generated';

type ActivityQueryError = ReturnType<typeof Apollo.useGetTaskActivityQuery>['error'];

const ACTIVITY_PAGE_SIZE = 25;

type ActivityRow = Apollo.ActivityItemFieldsFragment;
type ActivityPage = Apollo.ActivityPageFieldsFragment;

type TaskActivityQueryOptions = Omit<
  Parameters<typeof Apollo.useGetTaskActivityQuery>[0],
  'pollInterval' | 'variables'
>;
type WorkflowActivityQueryOptions = Omit<
  Parameters<typeof Apollo.useGetWorkflowActivityQuery>[0],
  'pollInterval' | 'variables'
>;

interface ActivityAnchorOptions {
  anchor?: UUID | null;
}

type TaskActivityOptions = ActivityAnchorOptions &
  TaskActivityQueryOptions & {
    /** Read one lane of the feed; rows from the other lane never enter it, pushed or added. */
    audience?: Apollo.ActivityAudience | null;
    /** Open on the oldest page and load newer ones forward, for a feed read from the top. */
    startAtOldest?: boolean;
  };
type WorkflowActivityOptions = ActivityAnchorOptions & WorkflowActivityQueryOptions;

interface UseActivityResult<TRow = ActivityRow> {
  activity: TRow[];
  addActivityItem: (row: TRow) => void;
  error: ActivityQueryError;
  fetchMoreActivity: () => Promise<unknown>;
  fetchPreviousActivity: () => Promise<unknown>;
  hasMore: boolean;
  hasPrevious: boolean;
  /**
   * The anchor whose window is currently on screen — the `anchor` variable that produced the
   * displayed page, not the one a pending fetch is chasing.
   */
  loadedAnchor: UUID | null;
  loading: boolean;
  /** A newer-page fetch is in flight (distinct from `loading`, which background refreshes also flip). */
  loadingMore: boolean;
  /** An older-page fetch is in flight. */
  loadingPrevious: boolean;
  totalCount: number;
}

/** The structural slice of an activity page that the merge logic reads — keeps tests light. */
interface ActivityPageLike<TRow extends { id: string; createdAt?: string } = { id: string }> {
  meta: {
    hasMore: boolean;
    hasPrevious: boolean;
    nextCursor: string | null;
    previousCursor: string | null;
    totalCount: number;
  };
  rows: TRow[];
}

/** Merge a newer page: append rows, adopt the newer edge, keep the older edge + totalCount. */
export function mergeFetchedActivityPage<TPage extends ActivityPageLike>(
  previousPage: TPage,
  incomingPage: TPage
): TPage {
  const mergedRows = mergeActivityRowsById(previousPage.rows, incomingPage.rows);
  const meta = {
    ...previousPage.meta,
    hasMore: incomingPage.meta.hasMore,
    nextCursor: incomingPage.meta.nextCursor,
    totalCount: previousPage.meta.totalCount,
  };
  return { ...incomingPage, meta, rows: mergedRows };
}

/** Merge an older page: prepend rows, adopt the older edge, keep the newer edge + totalCount. */
export function mergePreviousActivityPage<TPage extends ActivityPageLike>(
  previousPage: TPage,
  incomingPage: TPage
): TPage {
  const mergedRows = mergeActivityRowsById(previousPage.rows, incomingPage.rows);
  const meta = {
    ...previousPage.meta,
    hasPrevious: incomingPage.meta.hasPrevious,
    previousCursor: incomingPage.meta.previousCursor,
    totalCount: previousPage.meta.totalCount,
  };
  return { ...incomingPage, meta, rows: mergedRows };
}

export function sortActivityRows<TRow extends { id: string; createdAt?: string }>(
  rows: TRow[]
): TRow[] {
  return [...rows].sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return 0;
    return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
  });
}

/** Merge fresh row copies by id and preserve chronological display order. */
export function mergeActivityRowsById<TRow extends { id: string; createdAt?: string }>(
  existing: TRow[],
  incoming: TRow[]
): TRow[] {
  /**
   * Keep the loaded order, but prefer the fresh incoming copy for rows present in both
   * (so in-place server updates — e.g. a newly coalesced status event — are reflected), then
   * append any rows not yet loaded.
   */
  const incomingById = new Map(incoming.map(row => [row.id, row]));
  const seen = new Set(existing.map(row => row.id));
  const merged = existing.map(row => incomingById.get(row.id) ?? row);
  for (const row of incoming) if (!seen.has(row.id)) merged.push(row);
  return sortActivityRows(merged);
}

export function applyActivityFeedChangedEvent<TPage extends ActivityPageLike>(
  page: TPage,
  event: {
    activityItemId: string;
    changeType: Apollo.ActivityFeedChangeType;
    activityItem?: TPage['rows'][number] | null;
  }
): TPage {
  if (event.changeType === Apollo.ActivityFeedChangeType.Deleted) {
    const rowExists = page.rows.some(row => row.id === event.activityItemId);

    return {
      ...page,
      meta: { ...page.meta, totalCount: Math.max(0, page.meta.totalCount - 1) },
      rows: rowExists ? page.rows.filter(row => row.id !== event.activityItemId) : page.rows,
    };
  }

  if (!event.activityItem) return page;

  const rowExists = page.rows.some(row => row.id === event.activityItemId);
  if (event.changeType === Apollo.ActivityFeedChangeType.Updated) {
    if (!rowExists) return page;

    return { ...page, rows: mergeActivityRowsById(page.rows, [event.activityItem]) };
  }

  if (page.meta.hasMore && !rowExists)
    return { ...page, meta: { ...page.meta, totalCount: page.meta.totalCount + 1 } };

  return addActivityItemToPage(page, event.activityItem);
}

function addActivityItemToPage<TPage extends ActivityPageLike>(
  page: TPage,
  row: TPage['rows'][number]
): TPage {
  const rowExists = page.rows.some(existing => existing.id === row.id);
  const totalCount = rowExists ? page.meta.totalCount : page.meta.totalCount + 1;

  return {
    ...page,
    meta: { ...page.meta, totalCount },
    rows: mergeActivityRowsById(page.rows, [row]),
  };
}

interface StableActivityWindow {
  activity: ActivityRow[];
  hasMore: boolean;
  hasPrevious: boolean;
  /** A page has landed at least once — distinguishes "still empty" from "not loaded yet". */
  isLoaded: boolean;
  loadedAnchor: UUID | null;
  nextCursor: string | null;
  previousCursor: string | null;
  totalCount: number;
}

/**
 * The activity window on screen, held stable across anchored refetches.
 *
 * Following an anchor changes the query's variables, and Apollo has no data for the new window
 * until it arrives — going empty in that gap would unmount the virtualized list and throw away the
 * scroll position the reader is being carried to. The page and the anchor that produced it only
 * ever move forward together, so `loadedAnchor` always names the window being displayed.
 *
 * A different audience is a different timeline, so its rows are never held over: the reader would
 * see one lane's rows under the other lane's label until the new page arrived.
 */
function useStableActivityWindow({
  anchor,
  audience,
  page,
}: {
  anchor: UUID | null;
  audience: Apollo.ActivityAudience | null;
  page: ActivityPage | undefined;
}): StableActivityWindow {
  const lastLoadedRef = useRef<{
    anchor: UUID | null;
    audience: Apollo.ActivityAudience | null;
    page: ActivityPage | undefined;
  }>({
    anchor: null,
    audience: null,
    page: undefined,
  });
  if (page) lastLoadedRef.current = { anchor, audience, page };

  const heldPage =
    lastLoadedRef.current.audience === audience ? lastLoadedRef.current.page : undefined;
  const displayedPage = page ?? heldPage;
  const rows = displayedPage?.rows;
  const activity = useMemo(() => rows ?? [], [rows]);

  return {
    activity,
    hasMore: displayedPage?.meta.hasMore ?? false,
    hasPrevious: displayedPage?.meta.hasPrevious ?? false,
    isLoaded: Boolean(displayedPage),
    loadedAnchor: lastLoadedRef.current.anchor,
    nextCursor: displayedPage?.meta.nextCursor ?? null,
    previousCursor: displayedPage?.meta.previousCursor ?? null,
    totalCount: displayedPage?.meta.totalCount ?? 0,
  };
}

interface PaginatedActivityFeedConfig {
  anchor: UUID | null;
  audience: Apollo.ActivityAudience | null;
  entityId: UUID;
  entityKind: 'task' | 'workflow';
  error: ActivityQueryError;
  loading: boolean;
  page: ActivityPage | undefined;
  queryDocument: typeof Apollo.GetTaskActivityDocument | typeof Apollo.GetWorkflowActivityDocument;
  rootVariables: Apollo.GetTaskActivityQueryVariables | Apollo.GetWorkflowActivityQueryVariables;
  writeActivityPage: (update: (previousPage: ActivityPage) => ActivityPage) => void;
  skip?: boolean;
}

function usePaginatedActivityFeed({
  anchor,
  audience,
  entityId,
  entityKind,
  error,
  loading,
  page,
  queryDocument,
  rootVariables,
  skip,
  writeActivityPage,
}: PaginatedActivityFeedConfig): UseActivityResult {
  const client = useApolloClient();
  const loadingMoreRef = useRef(false);
  const loadingPreviousRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const {
    activity,
    hasMore,
    hasPrevious,
    isLoaded,
    loadedAnchor,
    nextCursor,
    previousCursor,
    totalCount,
  } = useStableActivityWindow({ anchor, audience, page });

  const fetchActivityPage = useCallback(
    async (variables: { before: string | null; cursor: string | null }) => {
      const { data: pageData } = await client.query({
        fetchPolicy: 'no-cache',
        query: queryDocument,
        variables: {
          ...rootVariables,
          anchor: null,
          before: variables.before,
          cursor: variables.cursor,
          limit: ACTIVITY_PAGE_SIZE,
        },
      });

      const incomingPage =
        entityKind === 'task' ? getTaskActivityPage(pageData) : getWorkflowActivityPage(pageData);
      if (!incomingPage) {
        if (!pageData)
          throw new Error(
            `${entityKind === 'task' ? 'Task' : 'Workflow'} activity page fetch returned no data (${entityKind} ${entityId}).`
          );
        return null;
      }

      const merge = variables.before ? mergePreviousActivityPage : mergeFetchedActivityPage;
      writeActivityPage(previousPage => merge(previousPage, incomingPage));
      return incomingPage;
    },
    [client, entityId, entityKind, queryDocument, rootVariables, writeActivityPage]
  );

  const fetchMoreActivity = useCallback(async () => {
    if (!nextCursor || loadingMoreRef.current || loadingPreviousRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      await fetchActivityPage({ before: null, cursor: nextCursor });
    } catch {
      enqueueSnackbar('Failed to load more activity. Please try again.', { variant: 'error' });
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [enqueueSnackbar, fetchActivityPage, nextCursor]);

  const fetchPreviousActivity = useCallback(async () => {
    if (!previousCursor || loadingMoreRef.current || loadingPreviousRef.current) return;
    try {
      loadingPreviousRef.current = true;
      setLoadingPrevious(true);
      await fetchActivityPage({ before: previousCursor, cursor: null });
    } catch {
      enqueueSnackbar('Failed to load earlier activity. Please try again.', { variant: 'error' });
    } finally {
      loadingPreviousRef.current = false;
      setLoadingPrevious(false);
    }
  }, [enqueueSnackbar, fetchActivityPage, previousCursor]);

  const addActivityItem = useCallback(
    (row: ActivityRow) => {
      if (audience && row.audience !== audience) return;
      writeActivityPage(previousPage => addActivityItemToPage(previousPage, row));
    },
    [audience, writeActivityPage]
  );

  useActivityFeedSubscription({
    audience,
    entityId,
    entityKind,
    skip,
    writeActivityPage,
  });

  return {
    activity,
    addActivityItem,
    error,
    fetchMoreActivity,
    fetchPreviousActivity,
    hasMore,
    hasPrevious,
    loadedAnchor,
    loading: loading && !isLoaded,
    loadingMore,
    loadingPrevious,
    totalCount,
  };
}

function useActivityFeedSubscription({
  audience,
  entityId,
  entityKind,
  skip,
  writeActivityPage,
}: {
  audience: Apollo.ActivityAudience | null;
  entityId: UUID;
  entityKind: 'task' | 'workflow';
  writeActivityPage: (update: (previousPage: ActivityPage) => ActivityPage) => void;
  skip?: boolean;
}): void {
  Apollo.useOnTaskActivityFeedChangedSubscription({
    onData: ({ data: subscriptionData }) => {
      const event = subscriptionData.data?.taskActivityFeedChanged;
      if (!event) return;
      writeActivityPage(previousPage =>
        getIsEventInLane(previousPage, event, audience)
          ? applyActivityFeedChangedEvent(previousPage, event)
          : previousPage
      );
    },
    onError: subscriptionError => {
      Sentry.captureException(subscriptionError, {
        extra: { subscription: 'TaskActivityFeedChanged', taskId: entityId },
      });
    },
    skip: skip || entityKind !== 'task',
    variables: { taskId: entityId },
  });

  Apollo.useOnWorkflowActivityFeedChangedSubscription({
    onData: ({ data: subscriptionData }) => {
      const event = subscriptionData.data?.workflowActivityFeedChanged;
      if (!event) return;
      writeActivityPage(previousPage => applyActivityFeedChangedEvent(previousPage, event));
    },
    onError: subscriptionError => {
      Sentry.captureException(subscriptionError, {
        extra: { subscription: 'WorkflowActivityFeedChanged', workflowId: entityId },
      });
    },
    skip: skip || entityKind !== 'workflow',
    variables: { workflowId: entityId },
  });
}

/**
 * Whether a pushed change belongs to the lane on screen. A deletion carries no row, so it counts
 * only when the row is loaded here.
 */
function getIsEventInLane(
  page: ActivityPage,
  event: { activityItemId: string; activityItem?: ActivityRow | null },
  audience: Apollo.ActivityAudience | null
): boolean {
  if (!audience) return true;
  if (event.activityItem) return event.activityItem.audience === audience;
  return page.rows.some(row => row.id === event.activityItemId);
}

function getTaskActivityPage(data: unknown): ActivityPage | null {
  if (!getIsRecord(data)) return null;
  const task = data.task;
  if (!getIsRecord(task)) return null;
  const activity = task.activity;
  return getIsActivityPage(activity) ? activity : null;
}

function getWorkflowActivityPage(data: unknown): ActivityPage | null {
  if (!getIsRecord(data)) return null;
  const workflow = data.workflow;
  if (!getIsRecord(workflow)) return null;
  const activity = workflow.activity;
  return getIsActivityPage(activity) ? activity : null;
}

function getIsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getIsActivityPage(value: unknown): value is ActivityPage {
  if (!getIsRecord(value)) return false;
  return Array.isArray(value.rows) && getIsRecord(value.meta);
}

/**
 * Reads a Task's unified activity feed (comments + status updates). Rows come back oldest-first
 * for display; `fetchMoreActivity` walks newer and `fetchPreviousActivity` walks older.
 */
export function useTaskActivity(taskId: UUID, options?: TaskActivityOptions): UseActivityResult {
  const client = useApolloClient();
  const { anchor = null, audience = null, startAtOldest = false, ...queryOptions } = options ?? {};
  const rootVariables = useMemo(
    (): Apollo.GetTaskActivityQueryVariables => ({
      anchor,
      audience,
      before: null,
      cursor: null,
      limit: ACTIVITY_PAGE_SIZE,
      startAtOldest,
      taskId,
    }),
    [anchor, audience, startAtOldest, taskId]
  );
  const { data, error, loading } = Apollo.useGetTaskActivityQuery({
    ...queryOptions,
    notifyOnNetworkStatusChange: true,
    variables: rootVariables,
  });

  const writeActivityPage = useCallback(
    (update: (previousPage: ActivityPage) => ActivityPage) => {
      client.cache.updateQuery<Apollo.GetTaskActivityQuery, Apollo.GetTaskActivityQueryVariables>(
        {
          query: Apollo.GetTaskActivityDocument,
          variables: rootVariables,
        },
        previous => {
          const previousPage = previous?.task?.activity;
          if (!previous || !previous.task || !previousPage) return previous;
          return {
            ...previous,
            task: { ...previous.task, activity: update(previousPage) },
          };
        }
      );
    },
    [client, rootVariables]
  );

  return usePaginatedActivityFeed({
    anchor,
    audience,
    entityId: taskId,
    entityKind: 'task',
    error,
    loading,
    page: data?.task?.activity,
    queryDocument: Apollo.GetTaskActivityDocument,
    rootVariables,
    skip: options?.skip,
    writeActivityPage,
  });
}

/** Reads a Workflow's unified activity feed. Same semantics as {@link useTaskActivity}. */
export function useWorkflowActivity(
  workflowId: UUID,
  options?: WorkflowActivityOptions
): UseActivityResult {
  const client = useApolloClient();
  const { anchor = null, ...queryOptions } = options ?? {};
  const rootVariables = useMemo(
    (): Apollo.GetWorkflowActivityQueryVariables => ({
      anchor,
      before: null,
      cursor: null,
      limit: ACTIVITY_PAGE_SIZE,
      workflowId,
    }),
    [anchor, workflowId]
  );
  const { data, error, loading } = Apollo.useGetWorkflowActivityQuery({
    ...queryOptions,
    notifyOnNetworkStatusChange: true,
    variables: rootVariables,
  });

  const writeActivityPage = useCallback(
    (update: (previousPage: ActivityPage) => ActivityPage) => {
      client.cache.updateQuery<
        Apollo.GetWorkflowActivityQuery,
        Apollo.GetWorkflowActivityQueryVariables
      >(
        {
          query: Apollo.GetWorkflowActivityDocument,
          variables: rootVariables,
        },
        previous => {
          const previousPage = previous?.workflow?.activity;
          if (!previous || !previous.workflow || !previousPage) return previous;
          return {
            ...previous,
            workflow: { ...previous.workflow, activity: update(previousPage) },
          };
        }
      );
    },
    [client, rootVariables]
  );

  return usePaginatedActivityFeed({
    anchor,
    audience: null,
    entityId: workflowId,
    entityKind: 'workflow',
    error,
    loading,
    page: data?.workflow?.activity,
    queryDocument: Apollo.GetWorkflowActivityDocument,
    rootVariables,
    skip: options?.skip,
    writeActivityPage,
  });
}
